import {
  AssessmentStageActionKind,
  ProjectPageGetProjectByIdDocument,
  RssAssessmentFragment,
  RssAssessmentType,
  StageActionAvailability,
  StageAvailableActionFragment,
  useCreateAssessmentStageRssActionMutation,
  useDeleteAssessmentStageRssActionMutation,
} from 'generated/graphql';
import { FC, useState } from 'react';
import {
  Modal,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { rssNotAvailableTooltipContent } from './helpers';
import SkillsSelfAssessmentActionForm, { SkillsSelfAssessmentActionFormValues } from './SkillsSelfAssessmentActionForm';

const getTooltipNote = (
  assessmentType: 'Soft skill' | 'Technical skill',
  availability?: StageActionAvailability
) => {
  if (availability === StageActionAvailability.NotAvailable)
    return rssNotAvailableTooltipContent(assessmentType);

  return '';
};

interface IEditSkillsSelfAssessmentActionModal {
  stageId: string;
  projectId: number;
  rssAssessments: RssAssessmentFragment[];
  availableActions: StageAvailableActionFragment[];
  onClose: () => void;
}

const EditSkillsSelfAssessmentActionModal: FC<IEditSkillsSelfAssessmentActionModal> =
  ({ stageId, projectId, rssAssessments, availableActions, onClose }) => {
    const [submitting, setSubmitting] = useState(false);
    const { handleMsgType } = useNotification();
    const [error, setError] = useState('');

    const [createRssAction] = useCreateAssessmentStageRssActionMutation({
      refetchQueries: [
        { query: ProjectPageGetProjectByIdDocument, variables: { id: projectId } },
      ],
    });

    const [deleteRssAction] = useDeleteAssessmentStageRssActionMutation({
      refetchQueries: [
        { query: ProjectPageGetProjectByIdDocument, variables: { id: projectId } },
      ],
    });

    const softSkillAssessmentExists = rssAssessments.some(
      rss => rss.type === RssAssessmentType.SoftSkill
    );

    const technicalSkillAssessmentExists = rssAssessments.some(
      rss => rss.type === RssAssessmentType.TechnicalSkill
    );

    const handleSubmit = async ({
      softSkillsChecked,
      technicalSkillsChecked,
    }: SkillsSelfAssessmentActionFormValues) => {
      if (!softSkillsChecked && !technicalSkillsChecked) {
        return setError('At least one option must be selected.');
      }

      const typesToCreate: RssAssessmentType[] = [];
      const typesToDelete: RssAssessmentType[] = [];

      if (!softSkillAssessmentExists && softSkillsChecked) {
        typesToCreate.push(RssAssessmentType.SoftSkill);
      }

      if (!technicalSkillAssessmentExists && technicalSkillsChecked) {
        typesToCreate.push(RssAssessmentType.TechnicalSkill);
      }

      if (softSkillAssessmentExists && !softSkillsChecked) {
        typesToDelete.push(RssAssessmentType.SoftSkill);
      }

      if (technicalSkillAssessmentExists && !technicalSkillsChecked) {
        typesToDelete.push(RssAssessmentType.TechnicalSkill);
      }

      if (typesToCreate.length === 0 && typesToDelete.length === 0) {
        onClose();
        return;
      }

      setSubmitting(true);
      try {
        for (const type of typesToDelete) {
          await deleteRssAction({
            variables: { stageId, type },
          });
        }
        for (const type of typesToCreate) {
          await createRssAction({
            variables: { stageId, type },
          });
        }
        handleMsgType({
          type: TNotification.success,
          message: 'Skills self-assessment action updated!',
        });
        onClose();
      } catch (_error) {
        handleMsgType({
          type: TNotification.error,
          message:
            'Oops, an error occurred while editing the Skills self-assessment action!',
        });
        setSubmitting(false);
      }
    };

    const softSkillAssessmentAvailability = availableActions.find(
      actionAvailability =>
        actionAvailability.kind === AssessmentStageActionKind.RssAssessmentSoft
    );

    const technicalSkillAssessmentAvailability = availableActions.find(
      actionAvailability =>
        actionAvailability.kind ===
        AssessmentStageActionKind.RssAssessmentTechnical
    );

    const softSkillTooltipNote = getTooltipNote(
      'Soft skill',
      softSkillAssessmentAvailability?.availability
    );

    const technicalSkillTooltipNote = getTooltipNote(
      'Technical skill',
      technicalSkillAssessmentAvailability?.availability
    );

    const softSkillsAssessmentAvailable =
      softSkillAssessmentAvailability?.availability !==
      StageActionAvailability.NotAvailable;

    const technicalSkillsAssessmentAvailable =
      technicalSkillAssessmentAvailability?.availability !==
      StageActionAvailability.NotAvailable;

    return (
      <Modal
        onClose={onClose}
        isOpen
        className="edit-skills-self-assessment-action-modal"
        header="Edit action"
      >
        <SkillsSelfAssessmentActionForm
          initialValues={{
            softSkillsChecked: softSkillAssessmentExists,
            technicalSkillsChecked: technicalSkillAssessmentExists,
          }}
          softSkillsAssessmentAvailable={softSkillsAssessmentAvailable}
          softSkillTooltipNote={softSkillTooltipNote}
          technicalSkillsAssessmentAvailable={
            technicalSkillsAssessmentAvailable
          }
          technicalSkillTooltipNote={technicalSkillTooltipNote}
          submitDisabled={submitting}
          error={error}
          title="Skill self-assessment"
          onSoftSkillsCheckboxChange={() => setError('')}
          onTechnicalSkillsCheckboxChange={() => setError('')}
          onCancel={onClose}
          onSubmit={handleSubmit}
        />
      </Modal>
    );
  };

export default EditSkillsSelfAssessmentActionModal;
